import Nd1BlogArticleShell from "../nd1/components/Nd1BlogArticleShell";
import { getPublicBlogPostByPath } from "../data/publicBlogPosts";

const PAGE_TITLE = "Hiring is broken: Understand the real problem, not the symptoms";
const PAGE_DESCRIPTION =
  "How AI-generated CVs flooded hiring with perfect fiction — and why JOBUWIN is building for genuine people and genuine jobs.";
const PAGE_PATH = "/blog/hiring-is-broken";

function HiringIsBrokenBlogPage() {
  const post = getPublicBlogPostByPath(PAGE_PATH);

  return (
    <Nd1BlogArticleShell
      seo={{
        title: PAGE_TITLE,
        description: PAGE_DESCRIPTION,
        path: PAGE_PATH,
        canonicalPath: PAGE_PATH,
        type: "article",
        schemaType: "article",
        imagePath: "/blog/hiring-is-broken-hero.png",
        imageAlt: "Zebras huddling together in a grassy savannah",
        articlePublishedTime: post?.publishedDate,
        articleModifiedTime: post?.publishedDate,
      }}
      eyebrow="Article"
      title={PAGE_TITLE}
    >
      <figure className="nd1-article-hero">
        <img
          src="/blog/hiring-is-broken-hero.png"
          alt="A group of zebras huddling together in a grassy savannah, symbolising unity and standing out from the crowd."
          width={1200}
          height={675}
          loading="eager"
          decoding="async"
        />
      </figure>

      <p>
        Do you remember what it was like to hire someone five or six years ago? Before this sudden, unnatural influx of
        “perfect” candidates? Did we ever stop and wonder what happened?
      </p>

      <p>
        Back then, we would post a job advert with a reasonably good job description and receive maybe 20 or 40 CVs. Out
        of those, perhaps 10 or 12 would genuinely match the skills and experience we were looking for. From there, we
        would shortlist 5 to 7 people worth interviewing and usually find the right hire.
      </p>

      <p>
        If we were efficient, we could have someone offered the role within two weeks and have them starting shortly
        after.
      </p>

      <p>
        As hiring managers, we received vastly different CVs. We could clearly see the differences between people. We
        could spot who had actually done the work, who had learned, and who had grown into the role. It was an organic
        process.
      </p>

      <p>
        Sure, it was never perfect. Some job descriptions were vague. Some applicants lacked the skills they claimed.
        Recruitment consultants were not always subject matter experts. But overall, the system worked reasonably well.
      </p>

      <p>
        One major difference was this: there was no pressure to get a CV submitted within seconds so it could sit
        “higher in the pile.” Applicants actually took the time to read the role. They tried to understand what the job
        involved. They reflected on their own experience and decided whether they were genuinely a fit.
      </p>

      <p>
        People valued their CVs. The information inside them represented years of learning, failures, successes, growth,
        and experience. A CV was not something casually sprayed across hundreds of job adverts with one click. People
        did some due diligence before applying.
      </p>

      <p>
        As professionals, we built experience over time. We learned, reflected, improved, failed, adapted, and grew.
        The CV became a byproduct of that journey.
      </p>

      <p>
        As hiring managers, this meant we got quality over quantity. We received applications from people who genuinely
        believed they had a real case to make because their actual experience aligned with our requirements.
      </p>

      <h2>Today, that world no longer exists.</h2>

      <p>
        Now we post a job and receive 300+ CVs. They all look the same. They all look impressive. They all look like
        the perfect candidate. Same keywords. Same structure. Same “impact.” Same “experience.” Maybe different
        formatting, but underneath it all, the organic journey behind those CVs is gone. It is now synthetic.
      </p>

      <h2>So what happened?</h2>

      <p>AI happened.</p>

      <p>
        Anyone can now generate a perfect CV. Not based on real life experience, but based on patterns extracted from
        millions of historical CVs, job descriptions, and professional profiles. Combine that with AI’s ability to
        creatively structure, optimise, and hallucinate content, and suddenly we as hiring managers are flooded with
        “perfect” applications.
      </p>

      <p>
        What once took years of real work, failures, learning, and growth can now be artificially manufactured in
        seconds. The CV is structured perfectly. The keywords are perfectly matched. The skills align 1:1 with the job
        description. The experience appears directly correlated. Everything is optimised, polished, spun, and shaped to
        look like the ideal candidate.
      </p>

      <p>
        And it can all be generated automatically for every single role someone applies for, regardless of whether it
        reflects the applicant’s real experience or not.
      </p>

      <p>These CVs are fiction. Perfect fiction.</p>

      <p>
        You can literally ask AI to generate a CV for a role you have never done, and it will produce something that
        looks completely real by borrowing patterns from thousands of actual professionals.
      </p>

      <p>And now hundreds of these AI-generated CVs are being sent to us every day.</p>

      <p>So instead of receiving 25 genuine applications, we receive 300 “perfect” ones.</p>

      <h2>The burden shifts.</h2>

      <p>
        We are now expected to figure out what is real and what is not. Which is nearly impossible. Even reviewing 50
        CVs properly is time consuming. Reviewing 300 thoroughly becomes a full time job.
      </p>

      <p>
        So naturally, we stop reading properly. We skim. We filter. We shortcut the process. And even when we
        shortlist someone, we often realise during interviews that the “perfect” CV does not match the real person
        sitting in front of us.
      </p>

      <h2>So here comes the next “solution.”</h2>

      <p>
        ATS systems. AI filtering. Automated shortlisting. Keyword matching. Scoring systems. Automated rejection
        emails. Cold, disconnected, and with no human touch.
      </p>

      <p>
        So now we have AI generating CVs, and more AI trying to filter those AI-generated CVs. But the filtering
        systems are still operating on artificially created inputs.
      </p>

      <p>And the result?</p>

      <p>Real people get buried.</p>

      <p>
        The people who have genuinely done the work. The people who have actually learned the craft. The people with
        real experience. They disappear into the noise.
      </p>

      <p>What the industry is building are systems that solve symptoms, not the root problem.</p>

      <p>
        More applications create more noise. More noise creates more filtering. More filtering creates more false
        positives. And the cycle continues while everyone involved becomes increasingly frustrated and disconnected from
        the process.
      </p>

      <p>It is not manageable. It is not scalable. And it is clearly not working.</p>

      <p>
        <strong>Hiring is broken.</strong>
      </p>

      <h2>So who is actually trying to fix this properly?</h2>

      <p>Because adding more AI on top of AI is not the answer.</p>

      <p>We do not need more noise. We need more truth. More genuineness. More realness.</p>

      <p>That is exactly what we are building with JOBUWIN.</p>

      <p>A platform designed to connect genuine people with genuine jobs. Real hiring managers with real candidates.</p>

      <p>Because if we continue down this path, we are not improving hiring.</p>

      <p>We are simply making it impossible.</p>

      <p className="nd1-article-cta">
        When you have a moment, have a look at{" "}
        <a href="https://www.jobuwin.com" rel="noopener noreferrer">
          www.jobuwin.com
        </a>
        .
      </p>
    </Nd1BlogArticleShell>
  );
}

export default HiringIsBrokenBlogPage;
